import type { FC } from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import type { StoreLocatorFilterItem } from "@locators/components/shared/types";

import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { Button } from "@jti/core-components/components/Button";
import { BREAKPOINT_SMALL_LARGE } from "@jti/fe-common";
import { useDrawer } from "@jti/ui/components/Drawer/providers";
import { useBodyScrollLock } from "@jti/ui/hooks/useBodyScrollLock";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFilterButton } from "../../../StoreLocatorFilterButton";

import { StoreLocatorFiltersAll } from "./StoreLocatorFiltersAll";

import styles from "./StoreLocatorFiltersMobile.module.scss";

type StoreLocatorFiltersMobile = {
  changeFilters?: (category: string, filter: StoreLocatorFilterItem) => void;
  checkFiltersStatus?: (category: string, filerKey: string) => boolean;
  applyFilters?: () => void;
};

export const StoreLocatorFiltersMobile: FC<StoreLocatorFiltersMobile> = ({
  applyFilters,
  changeFilters,
  checkFiltersStatus,
}) => {
  const isTablet = useMediaQuery({ minWidth: BREAKPOINT_SMALL_LARGE });
  const bodyRef = useRef<HTMLElement | null>(null);
  const { __ } = useTranslations();
  const { selectedFiltersNumber } = useStoreLocatorContext();
  const { toggleScroll } = useBodyScrollLock();
  const { isOpen, closeDrawer, openDrawer } = useDrawer();

  useLayoutEffect(() => {
    bodyRef.current = document.body;
  }, []);

  useEffect(() => {
    toggleScroll(isOpen);
  }, [isOpen, toggleScroll]);

  const handleToggleFilters = () => {
    isOpen ? closeDrawer() : openDrawer();
  };

  const handleApplyFilters = () => {
    applyFilters?.();
    closeDrawer();
  };

  return (
    <div>
      <StoreLocatorFilterButton
        isFiltersAvailable={true}
        isFiltersOpened={isOpen}
        onToggleFilters={handleToggleFilters}
        selectedFiltersNumber={selectedFiltersNumber}
      />
      {isOpen &&
        bodyRef.current &&
        createPortal(
          <div className={styles.filtersMobileWrapper}>
            <div className={styles.filtersHeaderContainer}>
              <button className={styles.filtersHeaderButton} onClick={handleToggleFilters}>
                <ChevronBackwardIcon />
                <h2>{__("headerlinks.button.findstore")}</h2>
              </button>
            </div>
            <StoreLocatorFiltersAll changeFilters={changeFilters} checkFiltersStatus={checkFiltersStatus} />
            <div className={styles.filterButtonContainer}>
              <Button
                variant="primary"
                size={isTablet ? "md" : "sm"}
                onClick={handleApplyFilters}
                data-testid="storeLocatorShowFilters"
              >
                {__("storelocator.all_filters.show_button")}
              </Button>
            </div>
          </div>,
          bodyRef.current
        )}
    </div>
  );
};
